import { Button, Card, Flex, List, Modal, Spin, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../../components/BackButton';
import OnboardingShell from '../../components/OnboardingShell';
import { useOnboarding } from '../../context/OnboardingContext';
import { PaymentHistoryItem } from '../../types/subscription';
import { formatCurrency, formatDateTime, maskCardPan } from '../../utils/format';
import { buildBotPath } from '../../utils/routes';

const HistoryPage = () => {
    const navigate = useNavigate();
    const { bot_id } = useParams();
    const {
        history,
        isHistoryLoading,
        loadHistory,
        subscriptionStatus,
        activeCard,
        payEarly,
        waitForEarlyPayment,
        pendingEarlyInvoiceId,
    } = useOnboarding();
    // The row whose early-payment HTTP call is in flight. Once the poll takes over,
    // `pendingEarlyInvoiceId` keeps the row in its processing state instead.
    const [submittingId, setSubmittingId] = useState<string | null>(null);

    useEffect(() => {
        loadHistory().catch(() => undefined);
    }, [loadHistory]);

    // The subscription is already paid more than one period ahead, so there is nothing to buy — mirror
    // the server's guard 5 and hide the button rather than let the user hit a 409.
    const alreadyExtended = (() => {
        const endTime = subscriptionStatus?.end_time;
        const periodSeconds = subscriptionStatus?.tariff?.period_seconds;

        if (!endTime || !periodSeconds) {
            return false;
        }

        const remainingSeconds = (new Date(endTime.replace(' ', 'T')).getTime() - Date.now()) / 1000;

        return remainingSeconds > periodSeconds;
    })();

    const handlePayEarly = (item: PaymentHistoryItem) => {
        if (!activeCard) {
            return;
        }

        const amountText = formatCurrency(item.amount, 'UZS');

        Modal.confirm({
            title: "Muddatidan oldin to'lash",
            centered: true,
            width: 'calc(100vw - 32px)',
            content: (
                <div>
                    <p>
                        Karta: {maskCardPan(activeCard.pan)} · Summa: {amountText}
                    </p>
                    <p>
                        To'lovni tasdiqlashdan oldin kartangizda <strong>{amountText}</strong> miqdorida
                        yetarli mablag' borligiga ishonch hosil qiling.
                    </p>
                    <p>
                        Mablag' yetarli bo'lmasa, to'lov amalga oshmaydi: obunangiz saqlanib qoladi,
                        kanaldan chiqarilmaysiz va to'lov {formatDateTime(item.payment_time)} da avtomatik
                        qayta uriniladi.
                    </p>
                    <p>
                        Qolgan kunlaringiz yo'qolmaydi — yangi muddat joriy obuna tugash sanasiga
                        qo'shiladi.
                    </p>
                </div>
            ),
            okText: "To'lash",
            cancelText: 'Bekor qilish',
            onOk: async () => {
                setSubmittingId(item.id);

                try {
                    // Only the HTTP call, so the modal closes within a second either way — a failure is
                    // already toasted by the axios interceptor.
                    await payEarly(item.id);
                } catch {
                    return;
                } finally {
                    setSubmittingId(null);
                }

                // After the modal closes: start polling without awaiting, so the row shows
                // "To'lov ishlanmoqda" while the poll runs and the user may navigate away.
                void waitForEarlyPayment(item.id);
            },
        });
    };

    return (
        <OnboardingShell
            title="To'lovlar tarixi"
            subtitle="Oxirgi yechib olishlar va kvitansiyalar ro'yxati."
            backButton={<BackButton onClick={() => navigate(buildBotPath(bot_id, '/onboarding/dashboard'))} />}
        >
            <Flex vertical gap={12}>
                <Card className="info-panel" bordered={false}>
                    {isHistoryLoading ? (
                        <div className="centered-loader">
                            <Spin size="large" />
                        </div>
                    ) : (
                        <List
                            dataSource={history}
                            locale={{
                                // A Tribute subscriber has no `invoice` rows in our system at all — the
                                // generic "no purchases yet" copy reads as if they never paid, when in
                                // fact their subscription is active and simply billed elsewhere.
                                emptyText: subscriptionStatus?.source === 'tribute'
                                    ? "To'lovlar tarixi bu yerda ko'rsatilmaydi — obunangiz Tribute orqali boshqariladi"
                                    : "Hali to'lovlar topilmadi",
                            }}
                            renderItem={(item) => {
                                const receiptId = (item.receipt_id || '').trim();
                                const displayDate = item.status === 'pending' && item.payment_time
                                    ? item.payment_time
                                    : item.created_time;

                                // The charge is being processed right now: either the schedule carries
                                // `in_process`, or a poll this session started is still running.
                                const isProcessing = Boolean(item.in_process) || item.id === pendingEarlyInvoiceId;

                                // A cancelled charge is frozen — the schedule behind it is gone and
                                // nobody will ever take the money. Showing `Kutilmoqda` here told a
                                // subscriber who had just unsubscribed that a payment was still due.
                                const statusTag = item.is_paid
                                    ? { color: 'success', label: 'To‘langan' }
                                    : item.status === 'cancelled'
                                        ? { color: 'default', label: 'Bekor qilingan' }
                                        : isProcessing
                                            ? { color: 'processing', label: "To'lov ishlanmoqda" }
                                            : { color: 'error', label: 'Kutilmoqda' };

                                // Client hint only; the server is the authority. Offer early payment on
                                // the next pending charge for an active subscriber who has a card and is
                                // not blocked.
                                const isNextPending = item.status === 'pending' && Boolean(item.payment_time)
                                    && (!subscriptionStatus?.next_payment
                                        || item.payment_time === subscriptionStatus.next_payment.pay_time);

                                const canPayEarly = isNextPending
                                    && Boolean(subscriptionStatus?.is_active)
                                    && subscriptionStatus?.source !== 'tribute'
                                    && !subscriptionStatus?.block
                                    && Boolean(activeCard)
                                    && !alreadyExtended;

                                return (
                                    <List.Item>
                                        <Flex vertical className="history-item" gap={4} style={{ width: '100%' }}>
                                            <Flex justify="space-between" align="center" gap={12} wrap>
                                                <Typography.Text strong>
                                                    {formatCurrency(item.amount, 'UZS')}
                                                </Typography.Text>
                                                <Tag color={statusTag.color}>
                                                    {statusTag.label}
                                                </Tag>
                                            </Flex>
                                            <Typography.Text className="section-text">
                                                {receiptId ? `${item.tariff_title} · ${receiptId}` : item.tariff_title}
                                            </Typography.Text>
                                            <Typography.Text className="section-text">
                                                {formatDateTime(displayDate)}
                                            </Typography.Text>
                                            {canPayEarly && (
                                                <Button
                                                    type="primary"
                                                    block
                                                    style={{ marginTop: 8 }}
                                                    disabled={isProcessing}
                                                    loading={submittingId === item.id}
                                                    onClick={() => handlePayEarly(item)}
                                                >
                                                    Muddatidan oldin to'lash
                                                </Button>
                                            )}
                                        </Flex>
                                    </List.Item>
                                );
                            }}
                        />
                    )}
                </Card>
            </Flex>
        </OnboardingShell>
    );
};

export default HistoryPage;
